import { appUrl } from "../tenant-url.js";

const RETURN_TO_KEY = "auth.returnTo";
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** Only root-relative application destinations; never an external URL or auth loop. */
export function safeAuthReturnTo(value: string | null | undefined): string | null {
  if (
    !value?.startsWith("/") ||
    value.startsWith("//") ||
    Array.from(value).some(
      (char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127 || char === "\\",
    )
  )
    return null;
  try {
    const url = new URL(value, "https://onshelf.invalid");
    if (url.origin !== "https://onshelf.invalid" || url.pathname.startsWith("//")) return null;
    if (
      !/^\/t\/[^/]+(?:\/|$)/.test(url.pathname) &&
      !["/accept-invitation", "/select-company", "/onboarding"].includes(url.pathname)
    )
      return null;
    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
}

export function invitationTokenFromReturnTo(value: string | null): string | undefined {
  const safe = safeAuthReturnTo(value);
  if (!safe) return undefined;
  const url = new URL(safe, "https://onshelf.invalid");
  if (url.pathname !== "/accept-invitation") return undefined;
  const token = url.searchParams.get("token");
  return token && token.length >= 16 && token.length <= 255 ? token : undefined;
}

export function rememberAuthReturnTo(value: string | null, email: string): void {
  try {
    const path = safeAuthReturnTo(value);
    if (path)
      localStorage.setItem(
        RETURN_TO_KEY,
        JSON.stringify({ path, email: email.trim().toLowerCase(), savedAt: Date.now() }),
      );
    else localStorage.removeItem(RETURN_TO_KEY);
  } catch {
    /* Storage may be disabled; the URL still carries the destination. */
  }
}

export function readAuthReturnTo(email?: string): string | null {
  try {
    const raw = localStorage.getItem(RETURN_TO_KEY);
    if (!raw) return null;
    // Continue verification links started before this fix was deployed.
    if (raw.startsWith("/")) return safeAuthReturnTo(raw);
    const saved = JSON.parse(raw) as { path?: string; email?: string; savedAt?: number };
    if (!saved.savedAt || Date.now() - saved.savedAt > MAX_AGE_MS) return null;
    if (email && saved.email !== email.trim().toLowerCase()) return null;
    return safeAuthReturnTo(saved.path);
  } catch {
    return null;
  }
}

export function clearAuthReturnTo(): void {
  try {
    localStorage.removeItem(RETURN_TO_KEY);
  } catch {
    /* Storage may be disabled. */
  }
}

export function authDestination(
  returnTo: string | null,
  tenantCode: string | null | undefined,
): string {
  // No active tenant does not prove there are no memberships (another tab may
  // have joined one). Resolve the company list before offering creation.
  return safeAuthReturnTo(returnTo) ?? (tenantCode ? appUrl(tenantCode) : "/select-company");
}
